import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Typography,
  Chip,
  Autocomplete,
  Grid,
  Card,
  CardMedia,
  IconButton
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { adminService } from '../../services/adminService';
import categorieService from '../../services/categorieService';

const ProductModal = ({ open, onClose, onSuccess, product = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    category: '',
    categorieId: null,
    images: [],
    boutiqueId: '',
    isActive: true
  });
  const [vendors, setVendors] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingBoutiques, setLoadingBoutiques] = useState(false);
  const [error, setError] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);


  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const categoriesData = await categorieService.getAllCategories();
      // console.log('Categories loaded in ProductModal:', categoriesData);
      setCategories(categoriesData || []);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadVendors();
      loadCategories();
      if (product) {
        // console.log('Product data:', product);
        
        // Réinitialiser selectedVendor au début
        setSelectedVendor('');
        setBoutiques([]); // Réinitialiser les boutiques
        
        setFormData({
          name: product.name || '',
          description: product.description || '',
          price: product.price || '',
          stock: product.stockQuantity || '',
          category: product.categorie?.libelle || product.category || '',
          categorieId: product.categorieId || product.categorie?.id || null,
          images: product.images || [],
          boutiqueId: product.boutiqueId || '',
          isActive: product.isActive !== false
        });
        setImagePreviews(product.images || []);
        
        // Charger les boutiques du vendeur si le produit a une boutique
        if (product.boutique?.user?.id) {
          // console.log('Setting vendor:', product.boutique.user.id);
          setSelectedVendor(product.boutique.user.id);
          // Charger les boutiques de manière asynchrone
          setTimeout(() => {
            loadBoutiques(product.boutique.user.id);
          }, 100);
        } else if (product.user?.id) {
          // Produit personnel (sans boutique) - selectedVendor reste vide
          // console.log('Product belongs to user directly:', product.user.id);
        }
      } else {
        setFormData({
          name: '',
          description: '',
          price: '',
          stock: '',
          category: '',
          categorieId: null,
          images: [],
          boutiqueId: '',
          isActive: true
        });
        setImagePreviews([]);
        setImageFiles([]);
        setSelectedVendor('');
        setBoutiques([]); // Réinitialiser les boutiques
      }
    }
  }, [product, open]);

  // Effet séparé pour gérer la sélection de catégorie quand les catégories sont chargées
  useEffect(() => {
    if (product && categories.length > 0 && formData.categorieId) {
      const selectedCategory = categories.find(cat => cat.id === formData.categorieId);
      if (selectedCategory) {
        setFormData(prev => ({
          ...prev,
          category: selectedCategory.libelle
        }));
      }
    }
  }, [categories, product, formData.categorieId]);

  const loadVendors = async () => {
    try {
      const data = await adminService.getUsers({ role: 'VENDOR' });
      setVendors(data.users || []);
    } catch (err) {
      console.error('Erreur lors du chargement des vendeurs:', err);
      setVendors([]);
    }
  };

  const loadBoutiques = async (vendorId) => {
    if (!vendorId) {
      setBoutiques([]);
      return;
    }
    
    setLoadingBoutiques(true);
    try {
      const data = await adminService.getBoutiques({ vendorId });
      setBoutiques(data.boutiques || []);
    } catch (err) {
      console.error('Erreur lors du chargement des boutiques:', err);
      setBoutiques([]);
    } finally {
      setLoadingBoutiques(false);
    }
  };

  const handleVendorChange = (e) => {
    const vendorId = e.target.value;
    setSelectedVendor(vendorId);
    setFormData(prev => ({ ...prev, boutiqueId: '' }));
    loadBoutiques(vendorId);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Vérifier le nombre d'images (max 5)
    const currentImageCount = formData.images ? formData.images.length : 0;
    if (currentImageCount + files.length > 5) {
      setError('Maximum 5 images autorisées pour un produit');
      return;
    }

    // Vérifier les types de fichiers
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setError('Seules les images (JPEG, JPG, PNG, GIF, WEBP) sont autorisées');
      return;
    }

    // Vérifier la taille (50MB max par fichier - sera compressé en WebP)
    const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Les fichiers ne doivent pas dépasser 50MB');
      return;
    }

    setError('');
    setImageFiles(prev => [...prev, ...files]);

    // Créer des aperçus
    const previewPromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve(event.target.result);
        };
        reader.readAsDataURL(file);
      });
    });

    // Attendre que tous les aperçus soient créés
    Promise.all(previewPromises).then(newPreviews => {
      setImagePreviews(prev => [...prev, ...newPreviews]);
    });

    // Uploader les fichiers
    setUploadingImages(true);
    try {
      const response = await adminService.uploadProductImages(files);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...response.imageUrls]
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'upload des images');
      // Retirer les fichiers ajoutés en cas d'erreur
      setImageFiles(prev => prev.slice(0, -files.length));
      setImagePreviews(prev => prev.slice(0, -files.length));
    } finally {
      setUploadingImages(false);
    }

    // Reset the input
    e.target.value = '';
  };

  const removeImage = async (index) => {
    // Supprimer l'image du serveur si elle a été uploadée
    const imageUrl = formData.images[index];
    if (imageUrl && imageUrl.includes('/imageProduit/')) {
      try {
        const filename = imageUrl.split('/').pop();
        await adminService.deleteProductImage(filename);
      } catch (err) {
        console.error('Erreur lors de la suppression de l\'image:', err);
      }
    }

    // Supprimer de tous les états
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Vérifier qu'il y a au moins 1 image
    if (formData.images.length === 0) {
      setError('Au moins 1 image est requise pour le produit');
      setLoading(false);
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        stock: formData.stock, // Garder le nom 'stock' pour l'envoi
        // Ajouter le vendeur sélectionné seulement s'il y en a un
        ...(selectedVendor && { vendorId: selectedVendor }),
        // Envoyer categorieId au lieu de category
        categorieId: formData.categorieId || null,
        // Convertir boutiqueId vide en null
        boutiqueId: formData.boutiqueId || null
      };
      // Supprimer category des données envoyées car on utilise categorieId
      delete dataToSend.category;
      
      // console.log('Data being sent to backend:', dataToSend);
      // console.log('categorieId value:', dataToSend.categorieId);

      if (product) {
        await adminService.updateProduct(product.id, dataToSend);
        onSuccess('Produit modifié avec succès');
      } else {
        await adminService.createProduct(dataToSend);
        onSuccess('Produit créé avec succès');
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {product ? 'Modifier le produit' : 'Créer un produit'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  name="name"
                  label="Nom du produit"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  fullWidth
                />

                <TextField
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  fullWidth
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    name="price"
                    label="Prix (FCFA)"
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                  <TextField
                    name="stock"
                    label="Stock"
                    type="number"
                    value={formData.stock}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Box>

                <Autocomplete
                  options={categories}
                  getOptionLabel={(option) => option.libelle || ''}
                  value={categories.find(cat => cat.id === formData.categorieId) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      category: newValue ? newValue.libelle : '',
                      categorieId: newValue ? newValue.id : null
                    }));
                  }}
                  loading={loadingCategories}
                  disabled={loadingCategories}
                  sx={{ 
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    '& .MuiAutocomplete-inputRoot': {
                      width: '100% !important',
                      minWidth: '100% !important'
                    },
                    '& .MuiAutocomplete-input': {
                      width: '100% !important'
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Catégorie"
                      placeholder={loadingCategories ? "Chargement des catégories..." : "Sélectionnez une catégorie"}
                      required
                      sx={{ 
                        width: '100%',
                        minWidth: '100%',
                        maxWidth: '100%',
                        '& .MuiInputBase-root': {
                          width: '100% !important',
                          minWidth: '100% !important'
                        },
                        '& .MuiInputBase-input': {
                          width: '100% !important',
                          minWidth: '100% !important'
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body1">
                        {option.libelle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {option.code}
                      </Typography>
                    </Box>
                  )}
                  noOptionsText={
                    loadingCategories
                      ? "Chargement des catégories..."
                      : "Aucune catégorie trouvée"
                  }
                />

                <Autocomplete
                  options={vendors}
                  getOptionLabel={(option) => option.name ? `${option.name} (${option.email || option.phone || ''})` : ''}
                  value={vendors.find(vendor => vendor.id === selectedVendor) || null}
                  onChange={(event, newValue) => {
                    const vendorId = newValue ? newValue.id : '';
                    setSelectedVendor(vendorId);
                    setFormData(prev => ({ ...prev, boutiqueId: '' }));
                    loadBoutiques(vendorId);
                  }}
                  sx={{ 
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    mt: 0.5,
                    '& .MuiAutocomplete-inputRoot': {
                      width: '100% !important',
                      minWidth: '100% !important'
                    },
                    '& .MuiAutocomplete-input': {
                      width: '100% !important'
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vendeur (optionnel)"
                      placeholder="Sélectionnez un vendeur"
                      sx={{ 
                        width: '100%',
                        minWidth: '100%',
                        maxWidth: '100%',
                        '& .MuiInputBase-root': {
                          width: '100% !important',
                          minWidth: '100% !important'
                        },
                        '& .MuiInputBase-input': {
                          width: '100% !important',
                          minWidth: '100% !important'
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body1">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {option.email || option.phone || ''}
                      </Typography>
                    </Box>
                  )}
                  noOptionsText="Aucun vendeur trouvé"
                />

                <FormControl fullWidth required={!!selectedVendor}>
                  <InputLabel>Boutique{selectedVendor ? '' : ' (optionnel)'}</InputLabel>
                  <Select
                    name="boutiqueId"
                    value={formData.boutiqueId}
                    onChange={handleChange}
                    label={`Boutique${selectedVendor ? '' : ' (optionnel)'}`}
                    disabled={(!selectedVendor && product?.user && !product?.boutique) || !selectedVendor || loadingBoutiques}
                  >
                    {loadingBoutiques ? (
                      <MenuItem disabled>Chargement des boutiques...</MenuItem>
                    ) : boutiques.length === 0 && selectedVendor ? (
                      <MenuItem disabled>Aucune boutique trouvée pour ce vendeur</MenuItem>
                    ) : (
                      boutiques.map((boutique) => (
                        <MenuItem key={boutique.id} value={boutique.id}>
                          {boutique.name} - {boutique.address}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {!selectedVendor && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {product?.user && !product?.boutique ? 
                        'Ce produit appartient directement à un utilisateur (sans boutique)' : 
                        'Sélectionnez un vendeur pour choisir une boutique'
                      }
                    </Typography>
                  )}
                  {selectedVendor && !loadingBoutiques && boutiques.length === 0 && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                      Ce vendeur n'a aucune boutique
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Images du produit
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="product-images"
                  multiple
                  type="file"
                  onChange={handleImageUpload}
                />
                <label htmlFor="product-images">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<Add />}
                    fullWidth
                    disabled={uploadingImages || (formData.images && formData.images.length >= 5)}
                  >
                    {uploadingImages ? 'Upload en cours...' : formData.images && formData.images.length >= 5 ? 'Limite de 5 images atteinte' : 'Ajouter des images'}
                  </Button>
                </label>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(imagePreviews.length > 0 ? imagePreviews : formData.images).map((image, index) => (
                  <Card key={index} sx={{ width: 100, height: 100, position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="100"
                      image={image}
                      alt={`Produit ${index + 1}`}
                      sx={{ objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeImage(index)}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' }
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Card>
                ))}
              </Box>

              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                {formData.images.length}/5 images • Formats acceptés: JPEG, JPG, PNG, GIF, WEBP (max 50MB - sera compressé en WebP)
              </Typography>

              {uploadingImages && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Upload en cours...
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Sauvegarde...' : (product ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProductModal;
